import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useCompleteTask, type TaskRow } from './useTasks';

/**
 * Closing a next action (#11).
 *
 * The database refuses to close one without either the next action or an explicit
 * close of the lead, so the form puts that choice in front of the person rather than
 * letting them discover it as an error. The default is "schedule the next one",
 * because that is what should normally happen — closing a lead is the exception and
 * should feel like one.
 */
export function CompleteTaskDialog({
  task,
  onClose,
}: {
  task: TaskRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const complete = useCompleteTask();

  const [outcome, setOutcome] = useState('');
  const [mode, setMode] = useState<'next' | 'close'>('next');
  const [nextDueAt, setNextDueAt] = useState('');
  const [nextType, setNextType] = useState('call');
  const [lostReason, setLostReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!task) return null;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      await complete.mutateAsync({
        taskId: task.id,
        leadId: task.lead_id,
        outcomeNote: outcome.trim() || undefined,
        ...(mode === 'next'
          ? { nextDueAt: nextDueAt ? new Date(nextDueAt).toISOString() : null, nextType }
          : { closeLead: true, lostReason: lostReason.trim() }),
      });
      setOutcome('');
      setNextDueAt('');
      setLostReason('');
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open title={t('tasks.complete')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <p className="text-sm text-slate-600">{task.leads?.full_name}</p>

        <Input
          label={t('tasks.outcome')}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
        />

        <fieldset className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-xs font-medium text-slate-600">{t('tasks.whatNext')}</legend>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="mode"
              checked={mode === 'next'}
              onChange={() => setMode('next')}
            />
            {t('tasks.scheduleNext')}
          </label>

          {mode === 'next' ? (
            <div className="grid gap-3 ps-6 sm:grid-cols-2">
              <Input
                label={t('leads.nextActionAt')}
                type="datetime-local"
                dir="ltr"
                required
                value={nextDueAt}
                onChange={(e) => setNextDueAt(e.target.value)}
              />
              <Select
                label={t('leads.activityType')}
                value={nextType}
                onChange={(e) => setNextType(e.target.value)}
                options={(['call', 'whatsapp', 'meeting', 'site_visit'] as const).map((x) => ({
                  value: x,
                  label: t(`activities.${x}` as 'activities.call'),
                }))}
              />
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="mode"
              checked={mode === 'close'}
              onChange={() => setMode('close')}
            />
            {t('tasks.closeLead')}
          </label>

          {mode === 'close' ? (
            <div className="ps-6">
              <Input
                label={t('leads.lostReason')}
                required
                hint={t('leads.lostReasonWhy')}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
            </div>
          ) : null}
        </fieldset>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={complete.isPending}
            disabled={mode === 'next' ? !nextDueAt : !lostReason.trim()}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
